import React, {
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RouteComponentProps } from 'react-router';
import { Box } from '@chakra-ui/react';

import { BodyProps, IAccordion } from 'constants/MultipleAcorddions';

import { MultipleAccordion, Loading } from 'components';

import { openNotification } from 'utils/notification';
import {
  AccessRight,
  AdditionalInformation,
  BasicInformation,
  Conclusions,
  Consent,
  OrientedInformation,
  Report,
  SecurityIncidents,
} from './components';
import ROPAData from 'models/Forms/ROPA';

import { readROPA, save, sendEmail } from 'services/ROPA';
import { getROPAFinishPosition } from 'services/ROPAConclusion';
import useEventBus from 'hooks/useEventBus';

export interface IROPAProps extends BodyProps {}

type stClient = (d: ROPAData) => ROPAData;

interface IRopaCTX {
  data: ROPAData;
  isDetails: boolean;
  handlePayload: (data: ROPAData | stClient) => void;
  client?: { name: string; id: React.ReactText };
  setClient?: React.Dispatch<React.SetStateAction<IRopaCTX['client']>>;
  load: boolean;
  setIsLoad: (value: boolean) => void;
}

export const ROPACtx = createContext<IRopaCTX>({
  data: new ROPAData(),
  isDetails: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePayload: (data: ROPAData | stClient) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsLoad: (value: boolean) => {},
  load: false,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ROPAPage: React.FC<RouteComponentProps<{ id?: string }>> = ({ match, history }) => {
  const eventBus = useEventBus();
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'ROPA - Registro das Operações de Tratamento');
  }, []);
  const id = useMemo(() => match.params.id, [match.params]);
  const [payload, setPayload] = useState(new ROPAData());
  const [client, setClient] = useState<IRopaCTX['client']>();
  const [isDone, setIsDone] = useState(!!id);
  const [completed, setCompleted] = useState<any>(false);
  const [load, setIsLoad] = useState<IRopaCTX['load']>(false);

  const [unfilledSteps, setUnfilledSteps] = useState<string[]>([
    '1 - Descrição',
    '2 - Informações Básicas',
    '3 - Análise Jurídica',
    '4 - Consentimento',
    '5 - Sistemas',
    '6 - Relatório de Impacto',
    '7 - Incidentes de Segurança',
  ]);
  const unfilledStepsRef = useRef(unfilledSteps);
  const payloadRef = useRef(payload);

  const [isLoading, setIsLoading] = useState(false);

  const accordionsItems = useMemo(
    () =>
      [
        {
          title: 'Descrição',
          // eslint-disable-next-line react/display-name
          body: props => <OrientedInformation {...props} />,
          submit: true,
        },
        {
          title: 'Informações Básicas',
          // eslint-disable-next-line react/display-name
          body: props => <BasicInformation {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Análise Jurídica',
          // eslint-disable-next-line react/display-name
          body: props => <AdditionalInformation {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Consentimento',
          // eslint-disable-next-line react/display-name
          body: props => <Consent {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Sistemas',
          // eslint-disable-next-line react/display-name
          body: props => <AccessRight {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Relatório de Impacto',
          // eslint-disable-next-line react/display-name
          body: props => <Report {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Incidentes de Segurança',
          // eslint-disable-next-line react/display-name
          body: props => <SecurityIncidents {...props} />,
          submit: true,
          // disabled: true,
        },
        {
          title: 'Conclusões',
          // eslint-disable-next-line react/display-name
          body: props => (
            <Conclusions
              unfilledSteps={unfilledSteps}
              isDone={completed}
              // loading={load}
              {...props}
            />
          ),
          submit: false,
          // disabled: !completed,
        },
      ] as IAccordion[],
    [unfilledSteps, isDone, load],
  );

  async function submit(dt: ROPAData, step: string, complete?: boolean) {
    // setIsload(!load);
    setIsLoad(true);
    if (complete && unfilledStepsRef.current.length > 1) {
      setIsDone(true);
      setTimeout(() => setIsDone(false), 3000);
      return true;
    }

    try {
      openNotification('Salvando...', 'info');

      const pay = {
        ...payloadRef.current,
        ...dt,
      };

      const { data } = await save(pay);
      payloadRef.current = { ...pay, id: data.id };
      setPayload({ ...pay, id: data.id });

      const finishForm = Number(step.slice(0, 1));

      if (data.id !== undefined) {
        const response = await getROPAFinishPosition(finishForm, data.id);
        if (response.data) {
          unfilledStepsRef.current = unfilledStepsRef.current.filter(i => i !== step);
          setUnfilledSteps(unfilledStepsRef.current);
        }
      }

      if (data.id) {
        if (complete) {
          openNotification('Concluindo...', 'info');

          const d = await readROPA(data.id);

          if (d.data) {
            setIsDone(true);
            await sendEmail(data.id);
            openNotification('Concluído com sucesso', 'success');
          } else {
            openNotification('Erro ao concluir avaliação', 'error');
          }
        } else {
          openNotification('Salvo com sucesso', 'success');
        }
      } else {
        openNotification('Erro ao salvar', 'error');
      }

      setPayload(pay);
      setIsLoad(false);
      return true;
    } catch (err) {
      setIsLoad(false);
      return false;
    }
  }

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setIsLoad(true);
      (async () => {
        try {
          const { data } = await readROPA(id);
          setCompleted(data.completed);
          setPayload(data);
          payloadRef.current = data;
          setUnfilledSteps([]);
          unfilledStepsRef.current = [];
        } catch (err) {
          openNotification('Ocorreu um erro ao carregar os dados', 'error');
        } finally {
          setTimeout(() => {
            setIsLoading(false);
            setIsLoad(false);
          }, 400);
        }
      })();
    }
  }, [id]);

  const handlePayload = useCallback((data: ROPAData | stClient) => {
    if (typeof data === 'function') {
      const dt = data(payloadRef.current);
      setPayload(dt);
      payloadRef.current = dt;
    } else {
      payloadRef.current = { ...payloadRef.current, ...data };
      setPayload({ ...payloadRef.current, ...data });
    }
  }, []);

  return (
    <Box w="100%">
      <Box m="0 auto" maxW="50rem" width="100%">
        {/* <Stack direction={['column', 'row']} spacing="1rem" mb="1rem">
          <Link mt="0.5em" onClick={history.goBack}>
            <Icon size={1.6} name={'arrowLeft'} iconColor={600} />
          </Link>
          <Heading mb="1rem" size="lg" as="h3">
            ROPA - Registro das Operações de Tratamento
          </Heading>
        </Stack> */}
        {isLoading ? (
          <Loading />
        ) : (
          <ROPACtx.Provider
            value={{
              data: payload,
              isDetails: !!id,
              setClient,
              client,
              handlePayload,
              load,
              setIsLoad,
            }}
          >
            <MultipleAccordion
              allowMultiple={completed && Object.keys(payload).length > 0}
              accordions={accordionsItems}
              runOnChange={submit}
              completed={completed}
            />
          </ROPACtx.Provider>
        )}
      </Box>
    </Box>
  );
};

export const ROPA = memo(ROPAPage);
